import React, { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
} from "reactstrap";
import { useFormik } from "formik";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import { productValidationSchema } from "utils";
import { ProductType } from "types";
import { useGetBranches } from "hooks/useBranches";
import { useGetCategories } from "hooks/useCategories";
import { Link, useNavigate } from "react-router-dom";
import { useGetSuppliers } from "hooks/useSuppliers";
import { useCreateProduct } from "hooks/useProduct";
import ProductImageInput from "./ProductImageInput";

import Flatpickr from "react-flatpickr";
import "react-datepicker/dist/react-datepicker.css";
import "assets/scss/datatables.scss";
import { AppRoutes } from "../../constants";
import classNames from "classnames";
import { BulkProductCreation, ExampleFileModal } from "./components";
import { BaseButton } from "Components";

const AddNewProduct = () => {
  // const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const navigate = useNavigate();
  const { mutate, isPending } = useCreateProduct();

  const [creationMode, setCreationMode] = useState<string>("manual");
  const [showExampleModal, setShowExampleModal] = useState<boolean>(false);

  const handleCancel = () => {
    navigate(AppRoutes.PRODUCTS);
  };

  // add branches, categories, and suppliers
  const { data: branchData } = useGetBranches();
  const { data: categoryData } = useGetCategories();
  const { data: supplierData } = useGetSuppliers();

  const formik = useFormik<ProductType>({
    initialValues: {
      id: 0,
      user_id: 0,
      name: "",
      description: "",
      cost_price: 0,
      selling_price: 0,
      sku: "",
      barcode: "",
      allow_taxes: true,
      weight: 0,
      weight_unit: "",
      quantity: 0,
      reorder_quantity: 0,
      category_id: 0,
      branch_id: 0,
      supplier_id: 0,
      sell_oos: false,
      expiry_date: "",
      files: [],
    },
    validationSchema: productValidationSchema,
    onSubmit: async (values) => {
      // Build the payload
      const payload: any = {
        name: values.name,
        description: values.description,
        cost_price: values.cost_price,
        selling_price: values.selling_price,
        sku: values.sku,
        allow_taxes: values.allow_taxes,
        weight: values.weight,
        weight_unit: values.weight_unit,
        quantity: values.quantity,
        reorder_quantity: values.reorder_quantity,
        category_id: values.category_id,
        branch_id: values.branch_id,
        supplier_id: values.supplier_id,
        sell_oos: values.sell_oos,
        files: values.files,
      };

      // Conditionally add barcode and expiry_date if they are provided
      if (values.barcode) {
        payload.barcode = values.barcode;
      }
      if (values.expiry_date) {
        payload.expiry_date = values.expiry_date;
      }

      // Use the mutate function from your hook
      mutate(payload, {
        onSuccess: (data) => {
          formik.resetForm();

          navigate(AppRoutes.PRODUCTS);
        },
      });
    },
  });

  return (
    <React.Fragment>
      <div className={`page-content`}>
        <Container fluid>
          {/* Breadcrumb */}
          <Breadcrumbs title="Products" breadcrumbItem="Add Product" />

          <Row>
            <Col xl="3" xxl="2" sm="4">
              <Card
                className={classNames(
                  {
                    "text-primary border-2 border-primary ":
                      creationMode === "manual",
                  },
                  "shadow-none rounded-3 cursor-pointer"
                )}
                onClick={() => {
                  setCreationMode("manual");
                }}
                style={{ cursor: "pointer" }}
              >
                <CardBody className="p-3">
                  <div>
                    <div className="float-end ms-2">
                      <i
                        className={classNames(
                          {
                            "bx bxs-check-circle text-primary":
                              creationMode === "manual",
                          },
                          "font-size-20"
                        )}
                      ></i>
                    </div>
                    <div className="avatar-xs me-3 mb-3">
                      <div className="avatar-title bg-transparent rounded">
                        <i className="bx bxs-file font-size-24 text-info"></i>
                      </div>
                    </div>
                    <div className="d-flex">
                      <div className="overflow-hidden me-auto">
                        <h5 className="font-size-14 text-truncate mb-1">
                          <Link to="#" className="text-body">
                            {" "}
                            Manual{" "}
                          </Link>
                        </h5>
                        <p className="text-muted text-truncate mb-0">
                          Create by filling form.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl="3" xxl="2" sm="4">
              <Card
                className={classNames(
                  {
                    "text-primary border-2 border-primary ":
                      creationMode === "bulk_creation",
                  },
                  "shadow-none rounded-3"
                )}
                onClick={() => {
                  setCreationMode("bulk_creation");
                }}
                style={{ cursor: "pointer" }}
              >
                <CardBody className="p-3">
                  <div>
                    <div className="float-end ms-2">
                      <i
                        className={classNames(
                          {
                            "bx bxs-check-circle text-primary":
                              creationMode === "bulk_creation",
                          },
                          "font-size-20"
                        )}
                      ></i>
                    </div>
                    <div className="avatar-xs me-3 mb-3">
                      <div className="avatar-title bg-transparent rounded">
                        <i className="bx bxs-folder font-size-24 text-warning"></i>
                      </div>
                    </div>
                    <div className="d-flex">
                      <div className="overflow-hidden me-auto">
                        <h5 className="font-size-14 text-truncate mb-1">
                          <Link to="#" className="text-body">
                            {" "}
                            Bulk Creation{" "}
                          </Link>
                        </h5>
                        <p className="text-muted text-truncate mb-0">
                          Upload CSV or excel sheets.
                        </p>
                      </div>
                      {/* <div className="align-self-end ms-2">
                        <p className="text-muted mb-0">3MB</p>
                      </div> */}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          {creationMode === "manual" && (
            <Form onSubmit={formik.handleSubmit} autoComplete="off">
              <Row>
                <Col xs={12}>
                  {/* Basic Information */}

                  <Card>
                    <CardBody>
                      <CardTitle tag="h4">Basic Information</CardTitle>
                      <Row>
                        <Col sm="6">
                          <div className="mb-3">
                            <Label htmlFor="name">Product Name *</Label>
                            <Input
                              id="name"
                              name="name"
                              type="text"
                              value={formik.values.name}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.name && !!formik.errors.name
                              }
                            />
                            {formik.touched.name && formik.errors.name && (
                              <FormFeedback>{formik.errors.name}</FormFeedback>
                            )}
                          </div>

                          <div className="mb-3">
                            <Label htmlFor="description">Description</Label>
                            <Input
                              id="description"
                              name="description"
                              type="textarea"
                              value={formik.values.description}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.description &&
                                !!formik.errors.description
                              }
                            />
                            {formik.touched.description &&
                              formik.errors.description && (
                                <FormFeedback>
                                  {formik.errors.description}
                                </FormFeedback>
                              )}
                          </div>
                        </Col>

                        <Col sm="6">
                          <div className="mb-3">
                            <Label htmlFor="cost_price">Cost Price *</Label>
                            <Input
                              id="cost_price"
                              name="cost_price"
                              type="number"
                              min="0"
                              step="0.01"
                              value={formik.values.cost_price}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.cost_price &&
                                !!formik.errors.cost_price
                              }
                            />
                            {formik.touched.cost_price &&
                              formik.errors.cost_price && (
                                <FormFeedback>
                                  {formik.errors.cost_price}
                                </FormFeedback>
                              )}
                          </div>

                          <div className="mb-3">
                            <Label htmlFor="selling_price">
                              Selling Price *
                            </Label>
                            <Input
                              id="selling_price"
                              name="selling_price"
                              type="number"
                              min="0"
                              step="0.01"
                              value={formik.values.selling_price}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.selling_price &&
                                !!formik.errors.selling_price
                              }
                            />
                            {formik.touched.selling_price &&
                              formik.errors.selling_price && (
                                <FormFeedback>
                                  {formik.errors.selling_price}
                                </FormFeedback>
                              )}
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col sm="6">
                          <div className="mb-3">
                            <Label htmlFor="sku">
                              SKU
                              <sub style={{ fontSize: "9px" }}>
                                (Stock Keeping Unit)
                              </sub>
                            </Label>
                            <Input
                              id="sku"
                              name="sku"
                              type="text"
                              value={formik.values.sku}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.sku && !!formik.errors.sku
                              }
                            />
                            {formik.touched.sku && formik.errors.sku && (
                              <FormFeedback>{formik.errors.sku}</FormFeedback>
                            )}
                          </div>

                          <div className="mb-3">
                            <Label htmlFor="barcode">Barcode</Label>
                            <Input
                              id="barcode"
                              name="barcode"
                              type="text"
                              value={formik.values.barcode}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.barcode &&
                                !!formik.errors.barcode
                              }
                            />
                            {formik.touched.barcode &&
                              formik.errors.barcode && (
                                <FormFeedback>
                                  {formik.errors.barcode}
                                </FormFeedback>
                              )}
                          </div>
                        </Col>

                        <Col sm="6">
                          <div className="mb-3">
                            <Label htmlFor="category_id">Category *</Label>
                            <Input
                              type="select"
                              name="category_id"
                              onChange={(e) =>
                                formik.setFieldValue(
                                  "category_id",
                                  e.target.value
                                )
                              }
                              value={formik.values.category_id}
                              invalid={
                                !!(
                                  formik.touched.category_id &&
                                  formik.errors.category_id
                                )
                              }
                            >
                              <option value="">Select Category...</option>
                              {categoryData &&
                                categoryData.map((category) => (
                                  <option key={category.id} value={category.id}>
                                    {category.name}
                                  </option>
                                ))}
                            </Input>
                            {formik.touched.category_id &&
                              formik.errors.category_id && (
                                <FormFeedback>
                                  {formik.errors.category_id}
                                </FormFeedback>
                              )}
                          </div>

                          <div className="mb-3">
                            <Label htmlFor="supplier_id">Supplier *</Label>
                            <Input
                              type="select"
                              name="supplier_id"
                              onChange={(e) =>
                                formik.setFieldValue(
                                  "supplier_id",
                                  e.target.value
                                )
                              }
                              value={formik.values.supplier_id}
                              invalid={
                                !!(
                                  formik.touched.supplier_id &&
                                  formik.errors.supplier_id
                                )
                              }
                            >
                              <option value="">Select Supplier...</option>
                              {supplierData &&
                                supplierData.map((supplier) => (
                                  <option key={supplier.id} value={supplier.id}>
                                    {supplier.name}
                                  </option>
                                ))}
                            </Input>
                            {formik.touched.supplier_id &&
                              formik.errors.supplier_id && (
                                <FormFeedback>
                                  {formik.errors.supplier_id}
                                </FormFeedback>
                              )}
                          </div>
                        </Col>
                      </Row>

                      {/* Expiry Date Picker taking full width */}
                      <div className="mb-3">
                        <Label htmlFor="expiry_date">Expiry Date</Label>
                        <Row>
                          <Col xs="8" sm="9" md="10">
                            <Flatpickr
                              id="expiry_date"
                              name="expiry_date"
                              value={formik.values.expiry_date || undefined}
                              placeholder="Select expiry date"
                              onChange={(date: Date[]) => {
                                // Check if a date is selected and format it as YYYY-MM-DD
                                const formattedDate = date.length
                                  ? date[0].toISOString().split("T")[0]
                                  : null;
                                formik.setFieldValue(
                                  "expiry_date",
                                  formattedDate
                                );
                              }}
                              options={{
                                dateFormat: "Y-m-d",
                                altFormat: "F j, Y",
                                altInput: true,
                              }} // Flatpickr date format
                              className="form-control"
                            />

                            {formik.touched.expiry_date &&
                              formik.errors.expiry_date && (
                                <span className="text-danger">
                                  {formik.errors.expiry_date}
                                </span>
                              )}
                          </Col>
                          <Col xs="4" sm="3" md="2">
                            <Button
                              onClick={(e) => {
                                e.preventDefault();
                                formik.setFieldValue("expiry_date", "");
                              }}
                              style={{ width: "100%" }}
                            >
                              Clear
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    </CardBody>
                  </Card>

                  {/* Inventory Information */}
                  <Card>
                    <CardBody>
                      <CardTitle tag="h4">Inventory Information</CardTitle>
                      <Row>
                        <Col sm="6">
                          <div className="mb-3">
                            <Label htmlFor="quantity">Quantity *</Label>
                            <Input
                              id="quantity"
                              name="quantity"
                              type="number"
                              min="0"
                              step="0.01"
                              value={formik.values.quantity}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.quantity &&
                                !!formik.errors.quantity
                              }
                            />
                            {formik.touched.quantity &&
                              formik.errors.quantity && (
                                <FormFeedback>
                                  {formik.errors.quantity}
                                </FormFeedback>
                              )}
                          </div>
                          <div className="mb-3">
                            <Label htmlFor="reorder_quantity">
                              Reorder Quantity
                            </Label>
                            <Input
                              id="reorder_quantity"
                              name="reorder_quantity"
                              type="number"
                              min="0"
                              step="0.01"
                              value={formik.values.reorder_quantity}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.reorder_quantity &&
                                !!formik.errors.reorder_quantity
                              }
                            />
                            {formik.touched.reorder_quantity &&
                              formik.errors.reorder_quantity && (
                                <FormFeedback>
                                  {formik.errors.reorder_quantity}
                                </FormFeedback>
                              )}
                          </div>
                        </Col>

                        <Col sm="6">
                          <div className="mb-3">
                            <Label htmlFor="branch_id">Branch *</Label>
                            <Input
                              type="select"
                              name="branch_id"
                              onChange={(e) =>
                                formik.setFieldValue(
                                  "branch_id",
                                  e.target.value
                                )
                              }
                              value={formik.values.branch_id}
                              invalid={
                                !!(
                                  formik.touched.branch_id &&
                                  formik.errors.branch_id
                                )
                              }
                            >
                              <option value="">Select Branch...</option>
                              {branchData &&
                                branchData.map((branch) => (
                                  <option key={branch.id} value={branch.id}>
                                    {branch.name}
                                  </option>
                                ))}
                            </Input>
                            {formik.touched.branch_id &&
                              formik.errors.branch_id && (
                                <FormFeedback>
                                  {formik.errors.branch_id}
                                </FormFeedback>
                              )}
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* Additional Information */}
                  <Card>
                    <CardBody>
                      <CardTitle tag="h4">Additional Information</CardTitle>
                      <Row>
                        <Col sm="6">
                          <div className="mb-3">
                            <Label htmlFor="weight">Weight</Label>
                            <Input
                              id="weight"
                              name="weight"
                              type="number"
                              min="0"
                              step="0.01"
                              value={formik.values.weight}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.weight && !!formik.errors.weight
                              }
                            />
                            {formik.touched.weight && formik.errors.weight && (
                              <FormFeedback>
                                {formik.errors.weight}
                              </FormFeedback>
                            )}
                          </div>

                          <div className="mb-3">
                            <Label htmlFor="weight_unit">Weight Unit *</Label>
                            <Input
                              id="weight_unit"
                              name="weight_unit"
                              type="text"
                              value={formik.values.weight_unit}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.weight_unit &&
                                !!formik.errors.weight_unit
                              }
                            />
                            {formik.touched.weight_unit &&
                              formik.errors.weight_unit && (
                                <FormFeedback>
                                  {formik.errors.weight_unit}
                                </FormFeedback>
                              )}
                          </div>
                        </Col>

                        <Col sm="6">
                          <div className="mb-3">
                            <Label htmlFor="allow_taxes">Allow Taxes *</Label>
                            <Input
                              id="allow_taxes"
                              name="allow_taxes"
                              type="checkbox"
                              checked={formik.values.allow_taxes}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.allow_taxes &&
                                !!formik.errors.allow_taxes
                              }
                            />
                            {formik.touched.allow_taxes &&
                              formik.errors.allow_taxes && (
                                <FormFeedback>
                                  {formik.errors.allow_taxes}
                                </FormFeedback>
                              )}
                          </div>

                          <div className="mb-3">
                            <Label htmlFor="sell_oos">
                              Sell When Out of Stock
                            </Label>
                            <Input
                              id="sell_oos"
                              name="sell_oos"
                              type="checkbox"
                              checked={formik.values.sell_oos}
                              onChange={formik.handleChange}
                              invalid={
                                formik.touched.sell_oos &&
                                !!formik.errors.sell_oos
                              }
                            />
                            {formik.touched.sell_oos &&
                              formik.errors.sell_oos && (
                                <FormFeedback>
                                  {formik.errors.sell_oos}
                                </FormFeedback>
                              )}
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* Product Images */}
                  <ProductImageInput setFieldValue={formik.setFieldValue} />
                </Col>
              </Row>
              {/* Button Section */}
              <div className="d-flex justify-content-center mt-3">
                <BaseButton
                  type="submit"
                  color="primary"
                  className="w-20 me-1"
                  style={{ width: "20%" }}
                  disabled={isPending}
                  loading={isPending}
                >
                  Submit
                </BaseButton>
                <BaseButton
                  color="secondary"
                  className="w-20 ms-1"
                  style={{ width: "20%" }}
                  onClick={() => {
                    handleCancel();
                  }}
                >
                  Cancel
                </BaseButton>
              </div>
            </Form>
          )}
          {creationMode === "bulk_creation" && <BulkProductCreation />}
        </Container>
      </div>
      <ExampleFileModal
        show={showExampleModal}
        onCloseClick={() => setShowExampleModal(false)}
      />
    </React.Fragment>
  );
};

export default AddNewProduct;
